import { subscribeToFeatureChanges } from '@core/State/FeatureFlags/helpers/subscribeToFeatureChanges'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { isReactNativeApp, subscribeToNative } from '@mobi/web-native-comms/web'
import { store } from '@core/Store'
import { setCoords } from '../Store'
import { startVenueTracking, stopVenueTracking, updateVenueLocation } from './venueTracker'

export interface GeoLocation {
  timestamp: number
  latitude: number
  longitude: number
  accuracy: number | null
  isoCode: string
  mocked?: boolean
  isPreciseAccuracy?: boolean
}

export function startVenueModeService(): void {
  if (!isReactNativeApp) {
    handleWeb()
  } else {
    handleNative()
  }
}

function handleNative() {
  let subscription: {
    dispose: () => void
  } | null = null

  subscribeToFeatureChanges(FeatureFlags.VENUE_MODE.key, isEnabled => {
    if (isEnabled) {
      subscription = subscribeToNative(
        'LBS_LAST_KNOWN_LOCATION_UPDATED',
        ({ latitude, longitude, isoCode, mocked, timestamp, accuracy, isPreciseAccuracy }) => {
          const geoLocation: GeoLocation = {
            timestamp,
            latitude: parseFloat(latitude),
            longitude: parseFloat(longitude),
            accuracy,
            isoCode,
            mocked,
            isPreciseAccuracy
          }
          
          updateVenueLocation(geoLocation)
          
          publish(
            timestamp,
            parseFloat(latitude),
            parseFloat(longitude),
            accuracy,
            isoCode,
            mocked,
            isPreciseAccuracy
          )
        }
      )
      
      startVenueTracking()
    } else {
      subscription?.dispose()
    }
  })
}

function handleWeb() {
  if (!('geolocation' in navigator)) return

  const options: PositionOptions = {
    maximumAge: 10 * 1000, // max possible age of cached position, in milliseconds
    timeout: 10 * 1000, // max time the device can take to return position, in milliseconds
    enableHighAccuracy: true, // ask for greater accuracy at the expense of time and resources
  }

  let subscriptionHandlerId: number | null = null
  let isVenueModeEnabled = false
  let isVenueModeBrowserEnabled = false

  subscribeToFeatureChanges(FeatureFlags.VENUE_MODE.key, isEnabled => {
    isVenueModeEnabled = isEnabled
    subscribeWeb()
  })
  subscribeToFeatureChanges(FeatureFlags.VENUE_MODE_BROWSER.key, isEnabled => {
    isVenueModeBrowserEnabled = isEnabled
    subscribeWeb()
  })

  function subscribeWeb() {
    if (isVenueModeEnabled && isVenueModeBrowserEnabled) {
      subscriptionHandlerId = navigator.geolocation.watchPosition(
        ({ coords: { latitude, longitude, accuracy }, timestamp }) => {
          const geoLocation: GeoLocation = {
            timestamp,
            latitude,
            longitude,
            accuracy,
            isoCode: '',
          }
          
          updateVenueLocation(geoLocation)
          publish(timestamp, latitude, longitude, accuracy, '')
        },
        null,
        options
      )

      startVenueTracking()
    } else {
      if (subscriptionHandlerId != null) navigator.geolocation.clearWatch(subscriptionHandlerId)
      subscriptionHandlerId = null
      
      stopVenueTracking()
    }
  }

  return
}

function publish(
  timestamp: number,
  latitude: number,
  longitude: number,
  accuracy: number | null,
  isoCode: string,
  mocked?: boolean,
  isPreciseAccuracy?: boolean
) {

  store.dispatch(
    setCoords({
      timestamp: new Date(timestamp).toISOString(),
      latitude,
      longitude,
      accuracy,
      isoCode,
      mocked,
      isPreciseAccuracy,
    })
  )
}